import { Action } from "redux"
import { all, call, put, takeEvery } from "redux-saga/effects"
import { withCallback } from "redux-saga-callback"

import apiClient from "@api/client"
import { iriFromIModelOrIRI } from "@api/entityTypeEndpointDefinitions"
import { IProject, ProjectState, ProjectVisibility } from "@api/schema"
import { IFormikActions } from "@basics/form"
import { UNKNOWN_REQUEST_ERROR } from "@redux/common/constants"
import { newSingleEntityUsecaseRequestRunningAction, newSingleEntityUsecaseRequestSuccessAction } from "@redux/common/entityRequest/actions"
import { EntityType } from "@redux/common/reduxTypes"
import { updateModelSuccessAction } from "@redux/common/scopedObject/actions"
import { SubmissionError } from "@services/submissionError"


/**
 * This enum defines the usecases around states of an IProject.
 */
export enum ProjectStateUsecases {
  /**
   * toggle the state of a project
   */
  ToggleStateProject = "_usecase_toggle_state_project",
  /**
   * Change the visibility of a project.
   */
  ChangeVisibility = "_usecase_change_project_visibility_",
}

// #region Actions

/**
 * Interface to toggle the state of an project between "active" and "deactivated".
 * Changes also from "inactive" to "active" after project fell inactive after
 * time without activity of the project team.
 */
interface IToggleProjectStateAction extends Action {
  actions: IFormikActions
  project: IProject
  type: ProjectStateUsecases.ToggleStateProject
}

/**
 * Creates an action to toggles the project state.
 *
 * If the project state is inactive or deactivated, an api request will be send to activate the project,
 * otherwise an api request will be send to deactivate the project.
 *
 * @todo Refactor: https://futureprojects.atlassian.net/browse/FCP-1817
 */
export const toggleProjectStateAction = (project: IProject, actions: IFormikActions): IToggleProjectStateAction => ({
  actions,
  project,
  type: ProjectStateUsecases.ToggleStateProject,
})

/**
 * Interface to change the visibility of a project.
 */
interface IChangeProjectVisibilityAction extends Action {
  actions: IFormikActions
  project: IProject
  visibility: ProjectVisibility
  type: ProjectStateUsecases.ChangeVisibility
}

/**
 * Creates an action to change the visibility of a project.
 */
export const changeProjectVisibilityAction = (project: IProject, visibility: ProjectVisibility, actions: IFormikActions): IChangeProjectVisibilityAction => ({
  actions,
  project,
  visibility,
  type: ProjectStateUsecases.ChangeVisibility,
})

// #endregion Actions

// #region Sagas
export function* projectStateWatcherSaga(): any {
  yield all([
    takeEvery(ProjectStateUsecases.ToggleStateProject, withCallback(projectStateSaga)),
    takeEvery(ProjectStateUsecases.ChangeVisibility, withCallback(projectVisibilitySaga)),
  ])
}

/**
 * Saga to change the state of a project.
 *
 * @param action to deactivate or activate a project
 * @returns changed project
 */
function* projectStateSaga(action: IToggleProjectStateAction) {
  const { onSuccess, setErrors, setSubmitting } = action.actions || {}

  // special (non-default) sagas for special (non-default) actions use their special usecaseKey (identical to action.type)
  const usecaseKey = action.type // ProjectStatesUsecases

  let project: IProject = null
  try {
    yield put(newSingleEntityUsecaseRequestRunningAction(EntityType.Project, usecaseKey))
    switch (action.project.state) {
      case ProjectState.Inactive:
      case ProjectState.Deactivated:
        project = yield call(apiClient.activateProject, action.project)
        break
      case ProjectState.Active:
        project = yield call(apiClient.deactivateProject, action.project)
        break
      // Ensures the switch is exhaustive:
      // "satisfies never" signalises the type checker, that "default" should never be reached.
      // If ProjectState is extended by another state a typescript error occurs, b/c the new
      // state must be added to this switch.
      default: action.project.state satisfies never
    }

    yield put(updateModelSuccessAction(EntityType.Project, project))

    yield put(newSingleEntityUsecaseRequestSuccessAction(EntityType.Project, usecaseKey, project))

    if (setSubmitting) {
      yield call(setSubmitting, false)
    }
    if (onSuccess) {
      yield call(onSuccess, project)
    }

    return project
  } catch (err) {

    const errorMessage = err instanceof Error ? err.message : UNKNOWN_REQUEST_ERROR

    if (setErrors) {
      if (err instanceof SubmissionError) {
        // errorHandling: setErrors is a function from FormikHelpers to set errors on a Formik-form
        yield call(setErrors, err.errors)
      } else {
        yield call(setErrors, { error: errorMessage })
      }
    }

    // if an error occurred: signalize that the currentScopeType-request has failed with the error message
    yield put(newSingleEntityUsecaseRequestRunningAction(EntityType.Project, usecaseKey, errorMessage))

    if (setSubmitting) {
      yield call(setSubmitting, false)
    }

    return null
  }
}

/**
 * Saga to change the visibility of a project.
 *
 * @param action to change the visibility of a project
 * @returns changed project
 */
function* projectVisibilitySaga(action: IChangeProjectVisibilityAction) {
  const { onSuccess, setErrors, setSubmitting } = action.actions || {}

  // special (non-default) sagas for special (non-default) actions use their special usecaseKey (identical to action.type)
  const usecaseKey = action.type // ProjectStatesUsecases

  let project: IProject = null
  try {
    yield put(newSingleEntityUsecaseRequestRunningAction(EntityType.Project, usecaseKey))
    project = yield call(apiClient.changeVisibility, iriFromIModelOrIRI(action.project), action.visibility)

    yield put(updateModelSuccessAction(EntityType.Project, project))

    yield put(newSingleEntityUsecaseRequestSuccessAction(EntityType.Project, usecaseKey, project))

    if (setSubmitting) {
      yield call(setSubmitting, false)
    }
    if (onSuccess) {
      yield call(onSuccess, project)
    }

    return project
  } catch (err) {

    const errorMessage = err instanceof Error ? err.message : UNKNOWN_REQUEST_ERROR

    if (setErrors) {
      if (err instanceof SubmissionError) {
        // errorHandling: setErrors is a function from FormikHelpers to set errors on a Formik-form
        yield call(setErrors, err.errors)
      } else {
        yield call(setErrors, { error: errorMessage })
      }
    }

    // if an error occurred: signalize that the currentScopeType-request has failed with the error message
    yield put(newSingleEntityUsecaseRequestRunningAction(EntityType.Project, usecaseKey, errorMessage))

    if (setSubmitting) {
      yield call(setSubmitting, false)
    }

    return null
  }
}
// #endregion Sagas